import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useAddYouTubeAssetMutation, useGetYouTubeCandidatesQuery } from '../../../../redux/api/addedAssetsApi';
import { addedErrorKind, addedErrorText } from './addedErrors';
import type { AddAssetRequest } from './addAssetDialogStore';
import { defaultYouTubePick, youtubeLinkState } from './youtubeLinkState';

/**
 * The state of the YouTube tab (design a2, a3): the videos of the connected channel with the best
 * match picked, or a pasted link; "Use this video" sends the pick (`videoId`) or the link (`url`).
 * With no connected channel, or no video on it, the tab opens on the link field.
 */
export function useYouTubeAdd(request: AddAssetRequest, onAdded: () => void) {
  const intl = useIntl();
  const { accountId, catalogItemUuid, themeId } = request;
  const {
    data,
    isLoading,
    error: loadError,
    refetch,
  } = useGetYouTubeCandidatesQuery({ accountId, catalogItemUuid, themeId });
  const [add, addState] = useAddYouTubeAssetMutation();
  const candidates = useMemo(() => data?.candidates ?? [], [data]);
  const hasList = data?.connected === true && candidates.length > 0;

  const [mode, setMode] = useState<'list' | 'paste' | null>(null);
  const shownMode = mode ?? (hasList ? 'list' : 'paste');
  const [picked, setPicked] = useState<string | null>(null);
  useEffect(() => {
    setPicked((current) => current ?? defaultYouTubePick(candidates));
  }, [candidates]);
  const [link, setLink] = useState('');
  const linkState = useMemo(() => youtubeLinkState(link, candidates), [candidates, link]);
  const [error, setError] = useState<string | null>(null);

  const changeLink = useCallback((value: string) => {
    setLink(value);
    setError(null);
  }, []);
  const pick = useCallback((videoId: string) => {
    setPicked(videoId);
    setError(null);
  }, []);
  const showPaste = useCallback(() => {
    setMode('paste');
    setError(null);
  }, []);
  const showList = useCallback(() => {
    setMode('list');
    setError(null);
  }, []);

  const canSubmit = shownMode === 'list' ? picked !== null : linkState.videoId !== null;
  const submit = useCallback(() => {
    if (!canSubmit) {
      return;
    }
    const body = shownMode === 'list' && picked !== null ? { videoId: picked } : { url: link.trim() };
    add({ accountId, catalogItemUuid, themeId, ...body })
      .unwrap()
      .then(onAdded)
      .catch((failure: unknown) => setError(addedErrorText(intl, addedErrorKind(failure), themeId)));
  }, [accountId, add, canSubmit, catalogItemUuid, intl, link, onAdded, picked, shownMode, themeId]);

  const pickedCandidate = candidates.find((candidate) => candidate.videoId === picked) ?? null;

  return {
    isLoading,
    loadError,
    retry: refetch,
    channel: data?.channel ?? null,
    candidates,
    hasList,
    mode: shownMode,
    showPaste,
    showList,
    picked,
    pickedCandidate,
    pick,
    link,
    linkState,
    changeLink,
    error,
    isAdding: addState.isLoading,
    canSubmit,
    submit,
  };
}
